import { createFileRoute, Link, Outlet, useMatches } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { ShieldCheck, Database, Terminal, Zap, AlertTriangle } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { useIsSuperAdmin } from '@/stores/authStore'
import { cn } from '@/lib/utils'
import { cardVariants } from '@/pages/admin/shared'

const SYSTEM_SUBTABS = [
  { to: '/admin/system/rbac', segment: 'rbac', label: 'RBAC Health', icon: ShieldCheck, color: 'text-emerald-500' },
  { to: '/admin/system/migrations', segment: 'migrations', label: 'Migrations', icon: Database, color: 'text-blue-500', superOnly: true },
  { to: '/admin/system/logs', segment: 'logs', label: 'System Logs', icon: Terminal, color: 'text-violet-500', superOnly: true },
  { to: '/admin/system/data-migration', segment: 'data-migration', label: 'Data Migration', icon: Zap, color: 'text-orange-500', superOnly: true },
] as const

export const Route = createFileRoute('/_authenticated/admin/system')({
  component: SystemLayout,
})

function SystemLayout() {
  const isSuperAdmin = useIsSuperAdmin()
  const matches = useMatches()
  const activeSubtab = matches[matches.length - 1]?.routeId?.split('/').pop() ?? 'rbac'

  const visibleTabs = SYSTEM_SUBTABS.filter(tab => !tab.superOnly || isSuperAdmin)

  return (
    <motion.div
      initial="hidden" animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
      className="space-y-5"
    >
      {/* Warning banner */}
      <motion.div variants={cardVariants} custom={0}>
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="flex items-start gap-3 p-4">
            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                System Maintenance Area
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                These operations modify core system data. All actions are audit-logged.
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Sub-tab navigation */}
      <motion.div variants={cardVariants} custom={1}>
        <div className="flex gap-1 p-1 rounded-xl bg-muted/50 border">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon
            const active = activeSubtab === tab.segment
            return (
              <Link
                key={tab.to}
                to={tab.to}
                className={cn(
                  'relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium',
                  'transition-all duration-200 flex-1 justify-center',
                  active
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-background/50',
                )}
              >
                <Icon className={cn('h-4 w-4', active ? tab.color : '')} />
                {tab.label}
              </Link>
            )
          })}
        </div>
      </motion.div>

      {/* Sub-tab content */}
      <Outlet />
    </motion.div>
  )
}
